import { defineStore } from 'pinia'
import { useAuthStore } from '@/features/account/session'
import apiService from '@/api'
import type { LorebookCreateData, LorebookUpdateData } from '@/api/contract'
import type { Lorebook } from '@/types/domain'

interface LorebooksState {
  lorebooks: Lorebook[]
  currentLorebook: Lorebook | null
  loading: boolean
  error: string | null
  lastFetched: number | null
  cacheDuration: number
  accountId: string | null
  generation: number
}

export const useLorebooksStore = defineStore('lorebooks', {
  state: (): LorebooksState => ({
    lorebooks: [],
    currentLorebook: null,
    loading: false,
    error: null,
    lastFetched: null,
    cacheDuration: 5 * 60 * 1000,
    accountId: null,
    generation: 0,
  }),

  getters: {
    getLorebookById: (state) => (id: number | string): Lorebook | null => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : Number(id)
      return state.lorebooks.find((lorebook) => lorebook.id === numericId) || null
    },
  },

  actions: {
    setAccount(id: string | null) {
      if (this.accountId === id) return
      this.accountId = id
      this.generation++
      this.lorebooks = []
      this.currentLorebook = null
      this.loading = false
      this.error = null
      this.lastFetched = null
    },

    assertLifetime(generation: number) {
      if (generation !== this.generation || this.accountId !== useAuthStore().userId) {
        throw new Error('The active account changed. Please try again.')
      }
    },

    async fetchLorebooks(forceRefresh = false) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      const now = Date.now()
      if (!forceRefresh && this.lastFetched && now - this.lastFetched < this.cacheDuration) {
        return this.lorebooks
      }

      this.loading = true
      this.error = null

      try {
        const response = await apiService.getLorebooks()
        this.assertLifetime(generation)
        this.lorebooks = response.lorebooks
        this.lastFetched = now

        if (this.currentLorebook) {
          this.currentLorebook = this.getLorebookById(this.currentLorebook.id) ?? this.currentLorebook
        }

        return this.lorebooks
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to fetch lorebooks'
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async fetchLorebook(id: number | string) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null

      try {
        const response = await apiService.getLorebook(id)
        this.assertLifetime(generation)
        const lorebook = response.lorebook
        this.mergeLorebook(lorebook)
        this.currentLorebook = lorebook
        return lorebook
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to fetch lorebook'
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async createLorebook(lorebookData: LorebookCreateData) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null

      try {
        const response = await apiService.createLorebook(lorebookData)
        this.assertLifetime(generation)
        const lorebook = response.lorebook
        this.mergeLorebook(lorebook)
        this.currentLorebook = lorebook
        this.lastFetched = Date.now()
        return lorebook
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to create lorebook'
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async updateLorebook(id: number | string, lorebookData: LorebookUpdateData) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null

      try {
        const response = await apiService.updateLorebook(id, lorebookData)
        this.assertLifetime(generation)
        const lorebook = response.lorebook
        this.mergeLorebook(lorebook)
        this.currentLorebook = lorebook
        this.lastFetched = Date.now()
        return lorebook
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to update lorebook'
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async deleteLorebook(id: number | string) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null

      try {
        await apiService.deleteLorebook(id)
        this.assertLifetime(generation)
        this.lorebooks = this.lorebooks.filter((lorebook) => lorebook.id !== Number(id))
        if (this.currentLorebook?.id === Number(id)) {
          this.currentLorebook = null
        }
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to delete lorebook'
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    mergeLorebook(lorebook: Lorebook | null | undefined) {
      if (!lorebook) return
      const normalized = lorebook
      const index = this.lorebooks.findIndex((item) => item.id === normalized.id)
      if (index === -1) {
        this.lorebooks.push(normalized)
      } else {
        this.lorebooks[index] = normalized
      }
    },

    clearCurrentLorebook() {
      this.currentLorebook = null
    },
  },
})
